import React, { useCallback, useRef, useState, useMemo } from "react";
import { useTheme } from "styled-components";
import IconButtonV2 from "../IconButtonV2";
import DropdownMenu from "../DropdownMenu";
import {
  Wrapper,
  Strip,
  SlidingIcons,
  LabelPill,
  StripRow,
} from "./styles";

/**
 * Standardized icon-action menu used in track rows, track info panels,
 * variation panes, and entity headers.
 *
 * Renders a pill of `IconButtonV2`s plus an optional trailing overflow
 * menu (hamburger). Action items follow the shape used by `DropdownMenu`:
 *
 *   { type, label, icon?, onClick, selected?, disabled?, tooltipText?, keepOpen? }
 *
 * Props:
 *   - actions           always-visible icons in the strip
 *   - menuActions       items rendered in the overflow `type="menu"` dropdown
 *                       (empty → no hamburger rendered)
 *   - align             "right" (track-row, default) | "left" (header)
 *                       controls strip anchor + slide direction
 *   - revealOnHover     animate the strip in via max-width transition
 *                       (track-row behavior). False = always visible.
 *   - visible           external control of reveal state (paired with
 *                       revealOnHover); defaults to true.
 *   - labelMode         "tooltip" (per-icon antd tooltip, default) |
 *                       "inline" (single LabelPill adjacent to strip)
 *   - menuMinWidth      passed to DropdownMenu
 *   - menuPlacement     "bottom" (default) | "top" — direction the
 *                       overflow menu opens. "top" is for strips pinned
 *                       to the bottom of the viewport (e.g. player bar).
 *   - transparent       drop the pill background so the strip blends into
 *                       its parent (e.g. dark player bar).
 *   - absolute          position the strip absolutely against the parent
 *                       (track-row default). False = inline flow (header).
 *   - wrapperRef        ref forwarded to the outer Wrapper element
 *   - buttonStyle       passed to each IconButtonV2
 *
 * Action entries may additionally include `popoverContent`,
 * `popoverTrigger`, `popoverPlacement`, and `popoverMouseEnterDelay`,
 * which are forwarded to IconButtonV2 to render a richer antd popover
 * (e.g., the multitrack-editor open affordance).
 */
const ActionIconStrip = ({
  actions = [],
  menuActions = [],
  align = "right",
  revealOnHover = false,
  visible = true,
  labelMode = "tooltip",
  menuMinWidth = "160px",
  menuPlacement = "bottom",
  transparent = false,
  absolute = false,
  wrapperRef = null,
  buttonStyle: buttonStyleProp = {},
  size = "sm",
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const menuAnchorRef = useRef(null);
  const theme = useTheme();

  // Strip stays expanded while the menu is open, regardless of the caller's
  // hover-driven `visible` flag. When revealOnHover is off (e.g. header,
  // player bar) the strip is always expanded.
  const expanded = !revealOnHover || visible || menuOpen;

  const [hoveredAction, setHoveredAction] = useState(null);
  const inlineLabel =
    labelMode === "inline" && hoveredAction
      ? hoveredAction.tooltipText ?? hoveredAction.label ?? hoveredAction.type
      : "";

  const buttonStyle = useMemo(() => ({
    transform: "none",
    padding: theme.iconButtonPad[size] ?? theme.iconButtonPad.sm,
    ...buttonStyleProp,
  }), [theme.iconButtonPad, buttonStyleProp, size]);

  const hasMenu = menuActions.length > 0;
  const hasIcons = actions.length > 0;

  // When an inline label pill is present, the outer StripRow owns absolute
  // positioning so the strip + pill move together. Otherwise the Strip itself
  // is the outermost element and takes the absolute position.
  const stripAbsolute = absolute && labelMode !== "inline";

  const strip = (
    <Strip $align={align} $absolute={stripAbsolute} $revealed={expanded} $transparent={transparent}>
      {hasMenu && (
        <>
          <span ref={menuAnchorRef} style={{ display: "inline-flex" }}>
            <IconButtonV2
              type="menu"
              size={size}
              selected={menuOpen}
              selectedBackground={false}
              hoverBackground={!transparent}
              onClick={() => setMenuOpen((prev) => !prev)}
              tooltip={labelMode === "tooltip" && !menuOpen}
              tooltipText="See more actions"
              label="Menu"
              style={buttonStyle}
            />
          </span>
          <DropdownMenu
            open={menuOpen}
            onClose={closeMenu}
            items={menuActions}
            minWidth={menuMinWidth}
            placement={menuPlacement}
            iconSize={size}
            anchorRef={menuAnchorRef}
            portal
          />
        </>
      )}
      {hasIcons && (
        <SlidingIcons
          $size={size}
          $visible={expanded}
          $animate={revealOnHover}
          onMouseLeave={
            labelMode === "inline" ? () => setHoveredAction(null) : undefined
          }
        >
          {actions.map((action) => {
            const key = `action-${action.type}`;
            const renderedBtn = action.render
              ? action.render({ size, style: buttonStyle })
              : null;
            const iconBtn = renderedBtn ? (
              React.cloneElement(renderedBtn, { key })
            ) : (
              <IconButtonV2
                key={key}
                type={action.type}
                size={size}
                selected={action.selected}
                selectedBackground={false}
                onClick={action.onClick}
                disabled={action.disabled}
                tooltip={labelMode === "tooltip"}
                tooltipText={action.tooltipText}
                label={action.label ?? action.type}
                popoverContent={action.popoverContent}
                popoverTrigger={action.popoverTrigger}
                popoverPlacement={action.popoverPlacement}
                popoverMouseEnterDelay={action.popoverMouseEnterDelay}
                popoverOverlayClassName={action.popoverOverlayClassName}
                popoverInnerStyle={action.popoverInnerStyle}
                style={buttonStyle}
              />
            );
            if (labelMode !== "inline") return iconBtn;
            return (
              <span
                key={key}
                onMouseEnter={() => setHoveredAction(action)}
                style={{ display: "inline-flex" }}
              >
                {iconBtn}
              </span>
            );
          })}
        </SlidingIcons>
      )}
    </Strip>
  );

  // Inline label mode: the pill is absolutely positioned and CSS-anchored
  // to whichever edge `align` specifies, so a single LabelPill works for
  // both align=left and align=right.
  const content =
    labelMode === "inline" ? (
      <StripRow $align={align} $absolute={absolute}>
        {strip}
        <LabelPill $align={align} $visible={!!inlineLabel}>
          <span>{inlineLabel}</span>
        </LabelPill>
      </StripRow>
    ) : (
      strip
    );

  return (
    <Wrapper $menuOpen={menuOpen} ref={wrapperRef}>
      {content}
    </Wrapper>
  );
};

export default React.memo(ActionIconStrip);
